import {
  ActsApiError,
  analyzeActDocument,
  createAct,
  deleteAct,
  downloadActDocument,
  getAct,
  getActs,
  restoreAct,
  updateAct,
  uploadActDocument,
  type ActAiExtractionResponse,
  type ActContract,
  type ActOrgan,
  type ActSessionType,
} from '@urbanik/api-client';
import {
  CanonicalModal,
  Button,
  CanonicalDonutChart,
  CanonicalCard,
  FileUpload,
  DocumentActions,
  FormActions,
  RowActions,
  CanonicalFilterBar,
  IconButton,
  OverflowMenu,
  PageSizeSelector,
  SummaryList,
  SummaryListItem,
  CanonicalPdfViewer,
  CanonicalFilterField,
  CanonicalPageHeader,
  StatusBadge,
  SearchField,
  CanonicalNotice,
  DropdownItem,
  ConfirmationDialog,
  DataTable,
  SelectField,
  Pagination,
  StatePanel,
  TextAreaField,
  InputField,
  canonicalStates,
  type DataColumn,
} from '@urbanik/ui';
import {
  Building2,
  CalendarDays,
  Download,
  Eye,
  FilePlusCorner,
  FileText,
  Info,
  ListFilter,
  Pencil,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { apiBase } from './api-base';
import './professional-acts.css';
const organs = [
  { value: 'general_assembly', label: 'Asamblea General' },
  { value: 'administration_council', label: 'Consejo de Administración' },
  { value: 'surveillance_council', label: 'Consejo de Vigilancia' },
] as const;
const sessions = [
  { value: 'ordinary', label: 'Ordinaria' },
  { value: 'extraordinary', label: 'Extraordinaria' },
  { value: 'informative', label: 'Informativa' },
] as const;
const organLabel = (value: ActOrgan) => organs.find((item) => item.value === value)?.label ?? value;
const sessionLabel = (value: ActSessionType) =>
  sessions.find((item) => item.value === value)?.label ?? value;
const blank = {
  organ: 'general_assembly' as ActOrgan,
  date: '',
  sessionType: 'ordinary' as ActSessionType,
  subject: '',
  resolutions: '',
  effectiveFrom: '',
  effectiveTo: '',
};
type State = 'loading' | 'empty' | 'error' | 'forbidden' | 'success';
export function ProfessionalActsView({
  canCreate = false,
  canUpload = false,
  canAnalyze = false,
  canUpdate = false,
  canDelete = false,
}: {
  canCreate?: boolean;
  canUpload?: boolean;
  canAnalyze?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}) {
  const [state, setState] = useState<State>('loading'),
    [items, setItems] = useState<readonly ActContract[]>([]),
    [selected, setSelected] = useState<ActContract | null>(null),
    [form, setForm] = useState(blank),
    [file, setFile] = useState<File | null>(null),
    [detailFile, setDetailFile] = useState<File | null>(null),
    [extraction, setExtraction] = useState<ActAiExtractionResponse | null>(null),
    [analyzing, setAnalyzing] = useState(false),
    [open, setOpen] = useState(false),
    [saving, setSaving] = useState(false),
    [message, setMessage] = useState(''),
    [search, setSearch] = useState(''),
    [year, setYear] = useState(''),
    [filter, setFilter] = useState(''),
    [sort, setSort] = useState<{
      columnId: 'date' | 'number';
      direction: 'ascending' | 'descending';
    }>({ columnId: 'date', direction: 'descending' }),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(10),
    [lifecycle, setLifecycle] = useState<'active' | 'trash'>('active'),
    [editing, setEditing] = useState<ActContract | null>(null),
    [pendingAction, setPendingAction] = useState<ActContract | null>(null),
    [pdfPreview, setPdfPreview] = useState<{ blob: Blob; fileName: string } | null>(null),
    [pdfPreviewState, setPdfPreviewState] = useState<'idle' | 'loading' | 'error'>('idle'),
    [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const load = () =>
    getActs(apiBase)
      .then((result) => {
        setItems(result.items);
        setState(result.items.length ? 'success' : 'empty');
      })
      .catch((error) => {
        if (error instanceof ActsApiError && error.kind === 'unauthorized') {
          location.hash = '#login';
          return;
        }
        setState(
          error instanceof ActsApiError && error.kind === 'forbidden' ? 'forbidden' : 'error',
        );
      });
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    if (!window.matchMedia?.('(max-width: 800px)').matches) return;
    const top = window.scrollY;
    window.scrollTo({ top, left: 0, behavior: 'auto' });
    document.documentElement.scrollLeft = 0;
    document.body.scrollLeft = 0;
  }, []);
  useEffect(() => {
    let active = true;
    setPdfPreview(null);
    if (!selected?.document || !pdfViewerOpen) {
      setPdfPreviewState('idle');
      return () => {
        active = false;
      };
    }
    setPdfPreviewState('loading');
    void downloadActDocument(apiBase, selected.id)
      .then((result) => {
        if (!active) return;
        setPdfPreview(result);
        setPdfPreviewState('idle');
      })
      .catch((error) => {
        if (!active) return;
        if (error instanceof ActsApiError && error.kind === 'unauthorized') {
          location.hash = '#login';
          return;
        }
        setPdfPreviewState('error');
      });
    return () => {
      active = false;
    };
  }, [pdfViewerOpen, selected?.document?.sha256, selected?.id]);
  useEffect(() => {
    setPdfViewerOpen(false);
  }, [selected?.id]);

  const analyze = async () => {
    if (!file) {
      setMessage('Selecciona primero el PDF que deseas analizar.');
      return;
    }
    if (file.type !== 'application/pdf' || file.size > 20 * 1024 * 1024) {
      setMessage('El documento debe ser PDF y pesar máximo 20 MB.');
      return;
    }
    setAnalyzing(true);
    setMessage('');
    try {
      const result = await analyzeActDocument(apiBase, file),
        proposal = result.proposal;
      setExtraction(result);
      setForm((current) => ({
        ...current,
        organ: proposal.organ ?? current.organ,
        sessionType: proposal.sessionType ?? current.sessionType,
        date: proposal.date ?? current.date,
        subject: proposal.subject ?? current.subject,
        resolutions: proposal.resolutions ?? current.resolutions,
        effectiveFrom: proposal.effectiveFrom ?? proposal.date ?? current.effectiveFrom,
        effectiveTo: proposal.effectiveTo ?? current.effectiveTo,
      }));
      setMessage('Propuesta aplicada. Revisa cada campo antes de generar el número institucional.');
    } catch (error) {
      if (error instanceof ActsApiError && error.kind === 'unauthorized') {
        location.hash = '#login';
        return;
      }
      setMessage(error instanceof Error ? error.message : 'No pudimos analizar el acta.');
    } finally {
      setAnalyzing(false);
    }
  };
  const visible = useMemo(
    () =>
      items.filter((act) => {
        const query = search.trim().toLocaleLowerCase('es');
        const matchesSearch =
          !query ||
          [
            act.number,
            act.subject,
            act.resolutions,
            organLabel(act.organ),
            sessionLabel(act.sessionType),
          ].some((value) => value.toLocaleLowerCase('es').includes(query));
        return (
          matchesSearch &&
          (year ? act.date.startsWith(year) : true) &&
          (filter ? act.organ === filter : true) &&
          (lifecycle === 'trash' ? Boolean(act.deletedAt) : !act.deletedAt)
        );
      }),
    [filter, items, lifecycle, search, year],
  );
  const years = useMemo(
    () =>
      Array.from(new Set(items.map((act) => act.date.slice(0, 4))))
        .sort()
        .reverse(),
    [items],
  );
  const activeItems = items.filter((act) => !act.deletedAt);
  const approvedCount = activeItems.filter((act) => act.documentStatus === 'approved').length;
  const pendingCount = activeItems.length - approvedCount;
  const orderedVisible = useMemo(
    () =>
      [...visible].sort((left, right) => {
        const leftValue = sort.columnId === 'date' ? left.date : left.number;
        const rightValue = sort.columnId === 'date' ? right.date : right.number;
        const result = leftValue.localeCompare(rightValue, 'es');
        return sort.direction === 'ascending' ? result : -result;
      }),
    [sort, visible],
  );
  const pages = Math.max(1, Math.ceil(orderedVisible.length / pageSize));
  const currentPage = Math.min(page, pages);
  const pagedActs = orderedVisible.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  useEffect(() => setPage(1), [filter, lifecycle, pageSize, search, year]);
  const chartData = organs.map((organ, index) => ({
    label: organ.label,
    value: activeItems.filter((act) => act.organ === organ.value).length,
    color: ['#0c497e', '#0ea5b4', '#87af0b'][index] ?? '#a8b0bd',
  }));
  const toggleSort = (columnId: string) => {
    if (columnId !== 'date' && columnId !== 'number') return;
    setSort((current) => ({
      columnId,
      direction:
        current.columnId === columnId && current.direction === 'descending'
          ? 'ascending'
          : 'descending',
    }));
    setPage(1);
  };
  const select = async (id: string) => setSelected((await getAct(apiBase, id)).act);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (editing) {
      setSaving(true);
      setMessage('');
      try {
        const updated = await updateAct(apiBase, editing.id, {
          sessionType: form.sessionType,
          subject: form.subject,
          resolutions: form.resolutions,
          effectiveFrom: form.effectiveFrom,
          effectiveTo: form.effectiveTo || null,
          expectedVersion: editing.version,
        });
        setItems((current) =>
          current.map((item) => (item.id === updated.act.id ? updated.act : item)),
        );
        setSelected(updated.act);
        setEditing(null);
        setOpen(false);
        setForm(blank);
        setMessage(`${updated.act.number} actualizada y auditada.`);
      } catch (error) {
        setMessage(error instanceof Error ? error.message : 'No pudimos actualizar el acta.');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!file) {
      setMessage('Selecciona el PDF firmado y aprobado.');
      return;
    }
    if (file.type !== 'application/pdf' || file.size > 20 * 1024 * 1024) {
      setMessage('El documento debe ser PDF y pesar máximo 20 MB.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const created = await createAct(apiBase, { ...form, effectiveTo: form.effectiveTo || null });
      const documented = await uploadActDocument(apiBase, created.act.id, file);
      setItems((current) => [documented.act, ...current]);
      setSelected(documented.act);
      setForm(blank);
      setFile(null);
      setOpen(false);
      setState('success');
      setMessage(`${documented.act.number} registrada con PDF firmado.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No pudimos registrar el acta.');
      await load();
    } finally {
      setSaving(false);
    }
  };
  const uploadSelected = async () => {
    if (!selected || !detailFile) {
      setMessage('Selecciona el PDF firmado y aprobado.');
      return;
    }
    if (detailFile.type !== 'application/pdf' || detailFile.size > 20 * 1024 * 1024) {
      setMessage('El documento debe ser PDF y pesar máximo 20 MB.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const documented = await uploadActDocument(apiBase, selected.id, detailFile);
      setSelected(documented.act);
      setItems((current) =>
        current.map((item) => (item.id === documented.act.id ? documented.act : item)),
      );
      setDetailFile(null);
      setMessage(
        `${documented.act.number}: PDF ${selected.document ? 'reemplazado' : 'archivado'} correctamente.`,
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No pudimos archivar el documento.');
    } finally {
      setSaving(false);
    }
  };
  const download = async (act: ActContract) => {
    try {
      const result = await downloadActDocument(apiBase, act.id),
        href = URL.createObjectURL(result.blob),
        anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = result.fileName;
      anchor.click();
      URL.revokeObjectURL(href);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No pudimos descargar el documento.');
    }
  };
  const startEdit = (act: ActContract) => {
    setEditing(act);
    setForm({
      organ: act.organ,
      date: act.date,
      sessionType: act.sessionType,
      subject: act.subject,
      resolutions: act.resolutions,
      effectiveFrom: act.effectiveFrom,
      effectiveTo: act.effectiveTo ?? '',
    });
    setFile(null);
    setExtraction(null);
    setOpen(true);
    setSelected(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const confirmLifecycleAction = async () => {
    if (!pendingAction) return;
    setSaving(true);
    setMessage('');
    try {
      const result = pendingAction.deletedAt
        ? await restoreAct(apiBase, pendingAction.id)
        : await deleteAct(apiBase, pendingAction.id);
      setItems((current) => current.map((item) => (item.id === result.act.id ? result.act : item)));
      if (selected?.id === result.act.id) setSelected(null);
      setMessage(
        result.act.deletedAt
          ? `${result.act.number} movida a la papelera. Puede restaurarse.`
          : `${result.act.number} restaurada correctamente.`,
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No pudimos completar la operación.');
    } finally {
      setSaving(false);
      setPendingAction(null);
    }
  };
  const columns: readonly DataColumn<ActContract>[] = [
    {
      id: 'order',
      header: 'N.º',
      accessor: (a) => String(orderedVisible.findIndex((item) => item.id === a.id) + 1),
      className: 'acts-order-column',
    },
    { id: 'number', header: 'Número institucional', accessor: (a) => a.number, sortable: true },
    {
      id: 'organ',
      header: 'Tipo de acta',
      accessor: (a) => `${organLabel(a.organ)} · ${sessionLabel(a.sessionType)}`,
    },
    { id: 'date', header: 'Fecha', accessor: (a) => a.date, sortable: true },
    { id: 'subject', header: 'Asunto principal', accessor: (a) => a.subject },
    {
      id: 'status',
      header: 'Estado',
      accessor: () => 'Aprobado',
      cell: () => <StatusBadge tone="success">Aprobado</StatusBadge>,
    },
    {
      id: 'registeredBy',
      header: 'Registrada por',
      accessor: (a) => `${a.registeredBy.displayName} · ${a.registeredBy.roles.join(', ')}`,
      cell: (a) => (
        <span className="acts-registrar">
          <strong>{a.registeredBy.displayName}</strong>
          <small>{a.registeredBy.roles.join(', ') || 'Sin rol asignado'}</small>
        </span>
      ),
    },
    {
      id: 'detail',
      header: 'Acciones',
      accessor: () => 'Acciones disponibles',
      cell: (a) => (
        <RowActions>
          <IconButton label={`Ver ${a.number}`} onClick={() => void select(a.id)}>
            <Eye aria-hidden="true" />
          </IconButton>
          {(!a.deletedAt && canUpdate) || canDelete ? (
            <OverflowMenu label={`Más acciones para ${a.number}`}>
              {!a.deletedAt && canUpdate ? (
                <DropdownItem icon={<Pencil aria-hidden="true" />} onSelect={() => startEdit(a)}>
                  Editar
                </DropdownItem>
              ) : null}
              {canDelete ? (
                <DropdownItem
                  icon={
                    a.deletedAt ? <RotateCcw aria-hidden="true" /> : <Trash2 aria-hidden="true" />
                  }
                  onSelect={() => setPendingAction(a)}
                >
                  {a.deletedAt ? 'Restaurar' : 'Mover a papelera'}
                </DropdownItem>
              ) : null}
            </OverflowMenu>
          ) : null}
        </RowActions>
      ),
    },
  ];
  return (
    <div className="shell-page professional-acts-page">
      <CanonicalPageHeader
        title="Actas"
        description="Documentos institucionales, resoluciones, PDF firmado y trazabilidad verificable."
        actions={
          canCreate && canUpload ? (
            <Button
              variant="primary"
              icon={<FilePlusCorner aria-hidden="true" />}
              onClick={() => {
                setEditing(null);
                setForm(blank);
                setFile(null);
                setOpen(true);
              }}
            >
              Nueva acta
            </Button>
          ) : null
        }
      />
      <CanonicalNotice message={message} onDismiss={() => setMessage('')} />
      <CanonicalModal
        open={open}
        eyebrow="M05.1 · Archivo institucional"
        title={editing ? `Editar ${editing.number}` : 'Nueva acta'}
        description={
          editing
            ? 'Corrige los datos editables. La identidad institucional y el PDF se conservan.'
            : 'Registra los datos y archiva el documento firmado y aprobado.'
        }
        onClose={() => {
          if (saving) return;
          setOpen(false);
          setEditing(null);
          setForm(blank);
          setFile(null);
        }}
      >
        <form className="professional-act-form" onSubmit={(event) => void submit(event)}>
          <div>
            <SelectField
              label={editing ? 'Órgano emisor (identidad institucional)' : 'Órgano emisor'}
              value={form.organ}
              onValueChange={(value) => setForm({ ...form, organ: value as ActOrgan })}
              options={organs}
              disabled={Boolean(editing)}
            />
            <small>El número se genera automáticamente al confirmar.</small>
          </div>
          <SelectField
            label="Tipo de sesión"
            value={form.sessionType}
            onValueChange={(value) => setForm({ ...form, sessionType: value as ActSessionType })}
            options={sessions}
          />
          <InputField
            label={editing ? 'Fecha institucional' : 'Fecha de la sesión'}
            type="date"
            value={form.date}
            onChange={(event) => setForm({ ...form, date: event.target.value })}
            required
            disabled={Boolean(editing)}
          />
          <InputField
            label="Inicio de vigencia"
            type="date"
            value={form.effectiveFrom}
            onChange={(event) => setForm({ ...form, effectiveFrom: event.target.value })}
            required
          />
          <InputField
            label="Fin de vigencia (opcional)"
            type="date"
            value={form.effectiveTo}
            onChange={(event) => setForm({ ...form, effectiveTo: event.target.value })}
          />
          <TextAreaField
            label="Asunto u orden principal"
            value={form.subject}
            onChange={(event) => setForm({ ...form, subject: event.target.value })}
            required
            maxLength={300}
          />
          <TextAreaField
            label="Resoluciones adoptadas"
            value={form.resolutions}
            onChange={(event) => setForm({ ...form, resolutions: event.target.value })}
            required
            maxLength={10000}
          />
          <FileUpload
            label="Acta firmada y aprobada (PDF)"
            file={file}
            disabled={Boolean(editing)}
            helpText={
              editing ? 'El PDF actual se conserva.' : 'PDF firmado y aprobado, máximo 20 MB.'
            }
            onError={setMessage}
            onFileChange={(nextFile) => {
              setFile(nextFile);
              setExtraction(null);
            }}
          />
          {file && !editing ? (
            <section
              className="act-form-pdf-preview"
              aria-label="Vista previa del PDF seleccionado"
            >
              <CanonicalPdfViewer file={file} fileName={file.name} />
            </section>
          ) : null}
          {canAnalyze && !editing ? (
            <section className="ai-extraction-panel" aria-live="polite">
              <div>
                <strong>Rellenar con asistencia de IA</strong>
                <p>
                  La propuesta no crea ni aprueba el acta. Debes contrastarla con el PDF y
                  corregirla.
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                disabled={!file || analyzing || saving}
                onClick={() => void analyze()}
              >
                {analyzing ? 'Analizando PDF…' : 'Analizar y proponer datos'}
              </Button>
              {extraction ? (
                <div className="ai-extraction-review">
                  <span className="surface-badge">Revisión humana obligatoria</span>
                  <p>
                    Modelo: <code>{extraction.model}</code>
                    {extraction.proposal.documentNumberFound
                      ? ` · Número leído: ${extraction.proposal.documentNumberFound}`
                      : ''}
                  </p>
                  {extraction.proposal.warnings.length ? (
                    <ul>
                      {extraction.proposal.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>
                      Sin advertencias automáticas. La verificación humana sigue siendo necesaria.
                    </p>
                  )}
                  {extraction.proposal.evidence.length ? (
                    <details>
                      <summary>Ver evidencias detectadas</summary>
                      <ul>
                        {extraction.proposal.evidence.map((evidence, index) => (
                          <li key={`${evidence.field}-${index}`}>
                            <strong>{evidence.field}</strong>
                            {evidence.page ? ` · página ${evidence.page}` : ''}: {evidence.excerpt}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
              ) : null}
            </section>
          ) : null}
          <FormActions>
            <Button variant="primary" disabled={saving}>
              {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Generar número y archivar'}
            </Button>
          </FormActions>
        </form>
      </CanonicalModal>
      {state !== 'success' ? (
        <StatePanel
          state={canonicalStates.find((item) => item.kind === state)!}
          onAction={() => void load()}
        />
      ) : (
        <section className="acts-layout">
          <div className="acts-content-column">
            <CanonicalFilterBar>
              <SearchField value={search} onChange={setSearch} />
              <CanonicalFilterField icon={<Building2 />}>
                <SelectField
                  label="Tipo de acta"
                  value={filter}
                  onValueChange={setFilter}
                  options={[{ value: '', label: 'Todos los órganos' }, ...organs]}
                />
              </CanonicalFilterField>
              <CanonicalFilterField icon={<CalendarDays />}>
                <SelectField
                  label="Año"
                  value={year}
                  onValueChange={setYear}
                  options={[
                    { value: '', label: 'Todos los años' },
                    ...years.map((value) => ({ value, label: value })),
                  ]}
                />
              </CanonicalFilterField>
              <CanonicalFilterField icon={<ListFilter />}>
                <SelectField
                  label="Estado"
                  value={lifecycle}
                  onValueChange={(value) => setLifecycle(value as 'active' | 'trash')}
                  options={[
                    { value: 'active', label: 'Actas vigentes' },
                    { value: 'trash', label: 'Papelera' },
                  ]}
                />
              </CanonicalFilterField>
            </CanonicalFilterBar>
            <div className="acts-table">
              <DataTable
                paginate={false}
                key={`${currentPage}-${search}-${filter}-${year}-${lifecycle}-${sort.columnId}-${sort.direction}`}
                rows={pagedActs}
                columns={columns}
                visibleColumnIds={new Set(columns.map((column) => column.id))}
                sort={sort}
                onSort={toggleSort}
                empty={<p>No hay actas para este órgano.</p>}
              />
              <footer className="ui-data-table-footer">
                <span>
                  Mostrando {pagedActs.length} de {orderedVisible.length} actas
                </span>
                <Pagination page={currentPage} pages={pages} onChange={setPage} />
                <PageSizeSelector
                  label="Registros por página"
                  value={pageSize}
                  options={[5, 10, 20, 50]}
                  onChange={setPageSize}
                />
              </footer>
            </div>
          </div>
          <aside className="acts-reference-aside" aria-label="Resumen de actas">
            <CanonicalCard title="Resumen de actas" icon={<FileText aria-hidden="true" />}>
              <SummaryList>
                <SummaryListItem label="Total de actas" value={activeItems.length} />
                <SummaryListItem label="Aprobadas" value={approvedCount} tone="success" />
                <SummaryListItem label="Pendientes" value={pendingCount} tone="warning" />
                <SummaryListItem label="En papelera" value={items.length - activeItems.length} />
              </SummaryList>
            </CanonicalCard>
            <CanonicalCard title="Tipos de actas">
              <CanonicalDonutChart
                data={chartData}
                ariaLabel="Distribución de actas por órgano emisor"
              />
            </CanonicalCard>
            <CanonicalCard className="acts-reference-notice">
              <Info aria-hidden="true" />
              <p>
                Las actas son documentos oficiales que respaldan las decisiones tomadas por los
                órganos de la cooperativa.
              </p>
            </CanonicalCard>
          </aside>
          <CanonicalModal
            open={Boolean(selected)}
            eyebrow="Expediente institucional"
            title={selected?.number ?? 'Detalle del acta'}
            description={selected ? selected.subject : ''}
            onClose={() => setSelected(null)}
          >
            {selected ? (
              <article className="act-detail">
                <header className="act-detail-header">
                  <div>
                    <p className="kicker">Expediente institucional</p>
                    <h2>{selected.number}</h2>
                    <p className="act-detail-subject">{selected.subject}</p>
                  </div>
                </header>
                <dl className="act-detail-meta">
                  <div>
                    <dt>Órgano</dt>
                    <dd>{organLabel(selected.organ)}</dd>
                  </div>
                  <div>
                    <dt>Sesión</dt>
                    <dd>{sessionLabel(selected.sessionType)}</dd>
                  </div>
                  <div>
                    <dt>Fecha del acta</dt>
                    <dd>{selected.date}</dd>
                  </div>
                  <div>
                    <dt>Vigencia</dt>
                    <dd>
                      {selected.effectiveFrom} — {selected.effectiveTo ?? 'Indefinida'}
                    </dd>
                  </div>
                  <div>
                    <dt>Registrada por</dt>
                    <dd>
                      {selected.registeredBy.displayName}
                      <small className="acts-registrar-role">
                        {selected.registeredBy.roles.join(', ') || 'Sin rol asignado'}
                      </small>
                    </dd>
                  </div>
                </dl>
                <section className="act-detail-section">
                  <span className="act-detail-section-number">01</span>
                  <div>
                    <h3>Resoluciones adoptadas</h3>
                    <p>{selected.resolutions}</p>
                  </div>
                </section>
                <section className="act-document-card">
                  <div className="act-document-card-heading">
                    <div>
                      <p className="kicker">Documento oficial</p>
                      <h3>{selected.document ? selected.document.fileName : 'PDF pendiente'}</h3>
                    </div>
                    <StatusBadge tone={selected.document ? 'success' : 'warning'}>
                      {selected.document ? 'Firmado y aprobado' : 'Pendiente'}
                    </StatusBadge>
                  </div>
                  {selected.document ? (
                    <div className="act-document-metadata">
                      <span>
                        Tamaño{' '}
                        <strong>{(selected.document.sizeBytes / 1024 / 1024).toFixed(2)} MB</strong>
                      </span>
                    </div>
                  ) : (
                    <p>El expediente todavía no contiene un acta firmada y aprobada.</p>
                  )}
                  {selected.document && pdfViewerOpen ? (
                    <CanonicalPdfViewer
                      file={pdfPreview?.blob ?? null}
                      fileName={pdfPreview?.fileName ?? selected.document.fileName}
                      loading={pdfPreviewState === 'loading'}
                      error={
                        pdfPreviewState === 'error'
                          ? 'No pudimos cargar la vista previa. Puedes descargar el PDF.'
                          : undefined
                      }
                      onDownload={() => void download(selected)}
                    />
                  ) : null}
                  {selected.document ? (
                    <DocumentActions>
                      <Button
                        variant="secondary"
                        icon={<Eye aria-hidden="true" />}
                        onClick={() => setPdfViewerOpen((value) => !value)}
                      >
                        {pdfViewerOpen ? 'Ocultar vista previa' : 'Previsualizar PDF'}
                      </Button>
                      <Button
                        variant="primary"
                        icon={<Download aria-hidden="true" />}
                        onClick={() => void download(selected)}
                      >
                        Descargar PDF firmado
                      </Button>
                    </DocumentActions>
                  ) : null}
                </section>
                {canUpload ? (
                  <div className="act-document-upload">
                    <FileUpload
                      label={
                        selected.document
                          ? 'Reemplazar PDF firmado'
                          : 'Cargar PDF firmado y aprobado'
                      }
                      file={detailFile}
                      onFileChange={setDetailFile}
                      onError={setMessage}
                      helpText="PDF, máximo 20 MB. La operación quedará auditada."
                    />
                    <Button
                      variant="secondary"
                      disabled={saving || !detailFile}
                      onClick={() => void uploadSelected()}
                    >
                      {saving
                        ? 'Archivando…'
                        : selected.document
                          ? 'Reemplazar documento'
                          : 'Archivar documento'}
                    </Button>
                  </div>
                ) : null}
              </article>
            ) : null}
          </CanonicalModal>
        </section>
      )}
      <ConfirmationDialog
        busy={saving}
        open={Boolean(pendingAction)}
        title={pendingAction?.deletedAt ? 'Restaurar acta' : 'Mover acta a papelera'}
        description={
          pendingAction?.deletedAt
            ? 'El acta volverá al listado vigente con su documento y trazabilidad.'
            : 'No se eliminará físicamente. El acta, su PDF y su auditoría se conservarán y podrá restaurarse.'
        }
        onConfirm={() => void confirmLifecycleAction()}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}
